import { v4 } from 'uuid';
import {
    AnnotationFrameChange,
    AnnotationPlane,
    AnnotationRecord,
} from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';

/**
 * The measurement record model (distance + angle). Patient frame, mm; display
 * conversion happens at the UI boundary. Plain serializable objects.
 */

/** Which kind of measurement the next placement will create. */
export type MeasurementKind = 'distance' | 'angle';

/** A distance between two patient-space points. */
export interface DistanceMeasurement extends AnnotationRecord {
    readonly kind: 'distance';
    readonly points: readonly [PointMm, PointMm];
    /** Cached straight-line distance in mm. */
    readonly distanceMm: number;
}

/** An angle at the middle vertex (`points[1]`) of three patient-space points. */
export interface AngleMeasurement extends AnnotationRecord {
    readonly kind: 'angle';
    readonly points: readonly [PointMm, PointMm, PointMm];
    /** Cached interior angle in degrees [0, 180]. */
    readonly angleDeg: number;
}

export type Measurement = DistanceMeasurement | AngleMeasurement;

/** An uncommitted placement, mirrored to recoil so the overlay can draw it. */
export interface MeasurementDraft {
    readonly kind: MeasurementKind;
    readonly plane: AnnotationPlane;
    /** Points placed so far (1..target-1), in mm. */
    readonly points: readonly PointMm[];
    /** Live cursor position for the rubber-band preview, or null. */
    readonly hoverPointMm: PointMm | null;
}

/** Number of points each kind requires before it is committed. */
export const MEASUREMENT_POINT_COUNT: Record<MeasurementKind, number> = {
    distance: 2,
    angle: 3,
};

/** Collision-free id. `uuid`, not `crypto`, which is banned repo-wide. */
export function createMeasurementId(): string {
    return `measurement-${v4()}`;
}

/** `measurement` with its plane and points moved by `change`. */
export function measurementInFrame<T extends Measurement>(
    measurement: T,
    change: AnnotationFrameChange
): T {
    return {
        ...measurement,
        plane: change.plane(measurement.plane),
        points: measurement.points.map(change.point),
    };
}

/** `draft` with its plane, points and hover point moved by `change`. */
export function measurementDraftInFrame(
    draft: MeasurementDraft,
    change: AnnotationFrameChange
): MeasurementDraft {
    return {
        ...draft,
        plane: change.plane(draft.plane),
        points: draft.points.map(change.point),
        hoverPointMm: draft.hoverPointMm && change.point(draft.hoverPointMm),
    };
}
